import { throttle } from 'throttle-debounce';
import { SETTINGS, BREAKPOINTS, CLASSES, ARIAS } from '../../utils/constants';
import initSearch from './search';

const MEGA_MENU_SELECTOR = '[data-navigation-menu="2"]';

const initHeader = () => {
  const headerEl = document.querySelector('[data-header]');
  if (!headerEl) return;
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP_LARGE}px)`);
  const announcementBarEl = document.querySelector('[data-announcement-bar]');
  const navToggle = headerEl.querySelector('[data-nav-toggle]');
  const navToggleText = navToggle.querySelector('[data-nav-toggle-text]');
  const navigationEl = headerEl.querySelector('[data-navigation]');
  const navigationWrapperEl = navigationEl.querySelector('[data-navigation-wrapper]');
  const navMenuEls = navigationEl.querySelectorAll('[data-navigation-menu]');

  const getStickyStatus = () => {
    if (!announcementBarEl) return;
    const announcementBarContainer = announcementBarEl.parentElement;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          headerEl.removeAttribute('data-sticky-active');
        } else {
          headerEl.setAttribute('data-sticky-active', 'true');
        }
      },
      { threshold: [0] }
    );
    if (announcementBarContainer) observer.observe(announcementBarContainer);
  };

  const setHeaderContentOffset = () => {
    const recalculate = () => {
      const announcementBarHeight = announcementBarEl ? announcementBarEl.getBoundingClientRect().height : 0;
      const headerHeight = headerEl.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-overlay-offset', `${announcementBarHeight + headerHeight}px`);
    };
    recalculate();
    const resizeObserver = new ResizeObserver(recalculate);
    resizeObserver.observe(headerEl);
    if (announcementBarEl) resizeObserver.observe(announcementBarEl);
  };

  const alignMegaMenus = () => {
    const megaMenus = headerEl.querySelectorAll(MEGA_MENU_SELECTOR);
    if (!megaMenus.length) return;

    const align = () => {
      if (!desktopMediaQuery.matches) {
        megaMenus.forEach(menu => {
          menu.style.removeProperty('left');
          menu.style.removeProperty('width');
        });
        return;
      }
      // clientWidth excludes the vertical scrollbar, unlike 100vw, which would
      // make the menu overflow the document and add a horizontal scrollbar.
      const documentWidth = document.documentElement.clientWidth;
      megaMenus.forEach(menu => {
        menu.style.setProperty('left', '0px', 'important');
        const offset = menu.getBoundingClientRect().left;
        menu.style.setProperty('left', `${-offset}px`, 'important');
        menu.style.setProperty('width', `${documentWidth}px`, 'important');
      });
    };

    align();
    window.addEventListener('resize', throttle(SETTINGS.THROTTLE_DELAY_SMALL, align));
    desktopMediaQuery.addEventListener('change', align);
  };

  const initMegaMenuHoverIntent = () => {
    const megaMenus = headerEl.querySelectorAll(MEGA_MENU_SELECTOR);
    if (!megaMenus.length) return;

    megaMenus.forEach(menu => {
      const li = menu.closest('li');
      if (!li) return;

      let closeTimeout = null;
      const cancelClose = () => {
        if (closeTimeout) {
          clearTimeout(closeTimeout);
          closeTimeout = null;
        }
      };
      const open = () => {
        if (!desktopMediaQuery.matches) return;
        cancelClose();
        li.setAttribute('data-submenu-open', 'true');
      };
      const scheduleClose = () => {
        cancelClose();
        closeTimeout = setTimeout(() => {
          li.removeAttribute('data-submenu-open');
          closeTimeout = null;
        }, SETTINGS.MEGA_MENU_CLOSE_DELAY);
      };

      li.addEventListener('mouseenter', open);
      li.addEventListener('mouseleave', scheduleClose);
      menu.addEventListener('mouseenter', cancelClose);
      menu.addEventListener('mouseleave', scheduleClose);
    });
  };

  const initHeaderHoverIntent = () => {
    if (!headerEl.hasAttribute('data-transparent-header')) return;

    let closeTimeout = null;
    headerEl.addEventListener('mouseenter', () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
      headerEl.setAttribute('data-header-hovered', 'true');
    });
    headerEl.addEventListener('mouseleave', () => {
      closeTimeout = setTimeout(() => {
        headerEl.removeAttribute('data-header-hovered');
        closeTimeout = null;
      }, SETTINGS.MEGA_MENU_CLOSE_DELAY);
    });
  };

  const setNavSearchBarHeight = () => {
    const navSearchBarEl = navigationEl.querySelector('[data-nav-search-bar]');
    if (!navSearchBarEl) return;
    const recalculate = () => {
      document.documentElement.style.setProperty('--nav-search-bar-height', `${navSearchBarEl.getBoundingClientRect().height}px`);
    };
    recalculate();
    new ResizeObserver(recalculate).observe(navSearchBarEl);
  };

  const closeSubNav = (subNav, level, closeBtn, handler) => {
    subNav.classList.remove(CLASSES.TRANSLATE_X_0);
    if (level === '2') {
      navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
    } else {
      const parentMenu = subNav.closest(MEGA_MENU_SELECTOR);
      subNav.classList.remove(CLASSES.SCROLLABLE);
      if (parentMenu) {
        parentMenu.classList.remove(CLASSES.OVERFLOW_HIDDEN);
      }
    }
    closeBtn.removeEventListener('click', handler);
  };

  const openSubNav = (subNav, level) => {
    if (!subNav) return;
    subNav.classList.add(CLASSES.TRANSLATE_X_0);
    navigationWrapperEl.classList.add(CLASSES.OVERFLOW_HIDDEN);
    if (level === '3') {
      const parentMenu = subNav.closest(MEGA_MENU_SELECTOR);
      if (parentMenu) {
        parentMenu.scrollTop = 0;
        subNav.classList.add(CLASSES.SCROLLABLE);
        parentMenu.classList.add(CLASSES.OVERFLOW_HIDDEN);
      }
    }

    const closeBtn = subNav.querySelector('[data-navigation-close]');
    if (closeBtn) {
      const handleClose = () => closeSubNav(subNav, level, closeBtn, handleClose);
      closeBtn.addEventListener('click', handleClose);
    }
  };

  const bindNavigationLinks = (links) => {
    links.forEach(link => {
      link.addEventListener('click', (evt) => {
        const targetAttr = link.getAttribute('target');
        if (targetAttr === '_blank') {
          evt.stopImmediatePropagation();
          return;
        }

        if (desktopMediaQuery.matches) return;

        const subNav = link.nextElementSibling;
        if (!subNav || !subNav.dataset.navigationMenu) return;

        evt.preventDefault();
        openSubNav(subNav, subNav.dataset.navigationMenu);
      }, { capture: true });
    });
  };

  const resetNavigation = () => {
    navMenuEls.forEach(menu => {
      menu.classList.remove(CLASSES.TRANSLATE_X_0);
      menu.scrollTop = 0;
    });
    navigationEl.scrollTop = 0;
    navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
  };

  const closeMobileNav = () => {
    navToggle.classList.remove('nav-open');
    navToggle.setAttribute(ARIAS.EXPANDED, 'false');
    navToggleText.textContent = navToggleText.dataset.navToggleText;
    navigationEl.classList.remove(CLASSES.TRANSLATE_X_0);
  };

  navToggle.addEventListener('click', () => {
    if (navToggle.classList.contains('nav-open')) {
      closeMobileNav();
      return;
    }
    navToggle.classList.add('nav-open');
    navToggle.setAttribute(ARIAS.EXPANDED, 'true');
    navToggleText.textContent = navToggleText.dataset.navToggleCloseText;
    navigationEl.classList.add(CLASSES.TRANSLATE_X_0);
    resetNavigation();
  });

  const navCloseBtns = navigationEl.querySelectorAll('[data-nav-close]');
  navCloseBtns.forEach(btn => btn.addEventListener('click', closeMobileNav));

  const SEARCH_ACTIVE_ATTR = 'data-search-active';
  const searchToggles = headerEl.querySelectorAll('[data-search-toggle]');
  const searchExpandedEl = headerEl.querySelector('[data-search-expanded]');
  const searchOverlayEl = searchExpandedEl ? searchExpandedEl.querySelector('.search-overlay') : null;

  const openSearch = () => {
    headerEl.setAttribute(SEARCH_ACTIVE_ATTR, 'true');
    searchToggles.forEach(toggle => toggle.setAttribute(ARIAS.EXPANDED, 'true'));
    document.body.classList.add(CLASSES.OVERFLOW_HIDDEN);
    if (searchOverlayEl) searchOverlayEl.classList.remove('hidden');
    const searchInputEl = searchExpandedEl.querySelector('input[type="search"]');
    if (searchInputEl) searchInputEl.focus();
  };

  const closeSearch = () => {
    headerEl.removeAttribute(SEARCH_ACTIVE_ATTR);
    searchToggles.forEach(toggle => toggle.setAttribute(ARIAS.EXPANDED, 'false'));
    document.body.classList.remove(CLASSES.OVERFLOW_HIDDEN);
    if (searchOverlayEl) searchOverlayEl.classList.add('hidden');
  };

  if (searchToggles.length && searchExpandedEl) {
    searchToggles.forEach(toggle => toggle.addEventListener('click', () => {
      if (headerEl.hasAttribute(SEARCH_ACTIVE_ATTR)) {
        closeSearch();
      } else {
        openSearch();
      }
    }));

    const searchCloseButtons = searchExpandedEl.querySelectorAll('[data-search-close]');
    searchCloseButtons.forEach(closeBtn => closeBtn.addEventListener('click', closeSearch));

    if (searchOverlayEl) searchOverlayEl.addEventListener('click', closeSearch);

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && headerEl.hasAttribute(SEARCH_ACTIVE_ATTR)) {
        closeSearch();
      }
    });

    const navSearchTrigger = navigationEl.querySelector('[data-nav-search-trigger]');
    if (navSearchTrigger) {
      navSearchTrigger.addEventListener('click', () => {
        closeMobileNav();
        openSearch();
      });
    }
  }

  const allNavLinks = navigationEl.querySelectorAll('a.navigation-item');
  bindNavigationLinks(allNavLinks);

  getStickyStatus();
  setHeaderContentOffset();
  alignMegaMenus();
  initMegaMenuHoverIntent();
  initHeaderHoverIntent();
  setNavSearchBarHeight();
  initSearch();
};

export default initHeader;
